import React, { useState, useCallback, useMemo } from 'react';
import { useRouteMatch } from 'react-router';
import { useSelector } from 'react-redux';
import { HeaderRight, Subtitle } from '@/components/atoms/Section/Section.style';
import { Title } from '@/components/atoms/Title';
import { Text } from '@/components/atoms/Text';
import { Table } from '@/components/atoms/Table';
import { Button } from '@/components/atoms/Button';
import RouterNavigationTabs from '@/components/organisms/RouterNavigationTabs';
import { routes } from '@/core/router/routes';
import RestaurantSelector from '@/components/molecules/RestaurantSelector';
import { FormattedMessage } from 'react-intl';
import { SvyIcon } from '@/components/savory/SvyIcon';
import { DropDown } from '@/components/atoms/DropDown';
import { getUserToken } from '@/redux/user';
import { useLazyGetCustomerListForFullExportQuery } from '@/services/innovorder/customer/customer.endpoints';
import { useGetBrandQuery, useGetBrandOptInsQuery } from '@/services/innovorder/brand/brand.endpoints';
import type { BrandOptIn } from '@/services/innovorder/brand/brand.type';
import { XlsxFile } from '@/utils/xslx';
import { downloadFile } from '@/utils/file/utils';
import { CustomerForExport } from '@/services/innovorder/customer/customer.types';
import { useCustomersTableVM } from './Customer.viewmodel';
import {
    Container,
    Header,
    HeaderContainer,
    HeaderTitle,
    OptionsButton,
    ActionsContainer,
    ExportButton,
} from './Customer.style';
import OptionsModal from './OptionsModal';
import { ImportCustomersModal } from '@/pages/Customer/List/ImportCustomersModal';

const formatDate = (date: string | Date | null | undefined): string => {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('fr-FR');
};

const centsToEuros = (value: number | string | null | undefined): number => {
    if (value === null || value === undefined) return 0;
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return 0;
    return num / 100;
};

type BrandFlags = { educationMode: boolean; tripAdvisor: boolean; birthdate: boolean };

const buildExportHeaders = (flags: BrandFlags, brandOptIns: BrandOptIn[]): string[] => {
    const headers = [
        'Identifiant client',
        'Email',
        'Prénom',
        'Nom',
        'Numéro de téléphone',
        'Total dépensé',
        'Nombre de commandes',
        'Panier moyen',
        'Dernière commande',
        'Fréquence',
        'Statut',
    ];

    if (flags.tripAdvisor) {
        headers.push("Nom d'utilisateur TripAdvisor");
    }

    if (flags.educationMode) {
        headers.push(
            'Solde',
            'N° badge',
            'N° convive',
            'Groupe',
            'Section',
            'Sous-section',
            'Type de compte',
            'Code tarif',
            'Code subvention',
            'Code admission',
            'Code régime tarifaire',
            "Date d'archivage",
            'Archivé',
            "Date d'arrivée",
            'Date de départ',
            'Badge bloqué',
        );
    }

    for (const optIn of brandOptIns) {
        headers.push(optIn.text);
    }

    if (flags.birthdate) {
        headers.push("Date d'anniversaire");
    }

    headers.push('Créé le');
    return headers;
};

const buildExportRow = (
    customer: CustomerForExport,
    flags: BrandFlags,
    brandOptIns: BrandOptIn[],
): (string | number)[] => {
    const row: (string | number)[] = [
        customer.customerId ?? '',
        customer.email ?? '',
        customer.firstName ?? '',
        customer.lastName ?? '',
        customer.phone ?? '',
        centsToEuros(customer.totalSpent),
        customer.totalOrders ?? 0,
        centsToEuros(customer.averagePurchase),
        formatDate(customer.lastOrder),
        customer.averageOrderPerWeek ?? '',
        customer.disabledAt ? 'Inactif' : 'Actif',
    ];

    if (flags.tripAdvisor) {
        row.push(customer.tripAdvisorUsername ?? '');
    }

    if (flags.educationMode) {
        const accountPaymentType =
            customer.accountType === 'BANKDEBIT' ? 'BANKDEBIT' : customer.accountPaymentType ?? '';
        row.push(
            centsToEuros(customer.balance),
            customer.badgeNumber ?? '',
            customer.studentNumber ?? '',
            customer.class ?? '',
            customer.section ?? '',
            customer.subSection ?? '',
            accountPaymentType,
            customer.tariffCode ?? '',
            customer.grantCode ?? '',
            customer.entranceFeeCode ?? '',
            customer.pricingRuleCode ?? '',
            formatDate(customer.archivedAt),
            customer.archivedAt && new Date(customer.archivedAt) < new Date() ? 'oui' : 'non',
            formatDate(customer.accessFrom),
            formatDate(customer.accessUntil),
            customer.isBadgeBlocked ? 'oui' : 'non',
        );
    }

    for (const optIn of brandOptIns) {
        row.push(customer.optIns && customer.optIns[optIn.optInId] ? 1 : 0);
    }

    if (flags.birthdate) {
        row.push(formatDate(customer.birthdate));
    }

    // API returns createdAt (camelCase) due to Sequelize underscored:true
    const createdAt = customer.created_at ?? (customer as Record<string, unknown>).createdAt;
    row.push(formatDate(createdAt as string));
    return row;
};

const Customer: React.FunctionComponent<React.PropsWithChildren> = () => {
    const { params } = useRouteMatch<{ brandId: string }>();
    const brandId = Number.parseInt(params.brandId, 10);
    const token = useSelector(getUserToken);

    const [showImportModal, setShowImportModal] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [tableKey, setTableKey] = useState(0);
    const handleOpenImportModal = useCallback(() => setShowImportModal(true), []);
    const handleCloseImportModal = useCallback(() => setShowImportModal(false), []);
    const handleImportSuccess = useCallback(() => setTableKey((prev) => prev + 1), []);

    const [getCustomerListForFullExport] = useLazyGetCustomerListForFullExportQuery();
    const { data: brand } = useGetBrandQuery({ brandId, token });
    const { data: brandOptIns } = useGetBrandOptInsQuery({ brandId, token });

    const getBrandFlags = useCallback((): BrandFlags => {
        const opts = brand?.options;
        return {
            educationMode: !!opts?.EDUCATION_MODE?.isActivated,
            tripAdvisor: !!opts?.TRIP_ADVISOR_USERNAME?.isActivated,
            birthdate: !!opts?.BIRTHDATE?.isActivated,
        };
    }, [brand]);

    const fetchCustomersForExport = useCallback(async () => {
        const result = await getCustomerListForFullExport({ brandId, token }).unwrap();
        const customers = Array.isArray(result) ? result : (result as { customers: CustomerForExport[] }).customers;
        return customers;
    }, [brandId, token, getCustomerListForFullExport]);

    const buildExportRows = useCallback(
        (customers: CustomerForExport[]) => {
            const flags = getBrandFlags();
            const optIns = brandOptIns ?? [];
            const headers = buildExportHeaders(flags, optIns);
            return [headers, ...customers.map((c) => buildExportRow(c, flags, optIns))];
        },
        [getBrandFlags, brandOptIns],
    );

    const handleExportXlsx = useCallback(async () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            const customers = await fetchCustomersForExport();
            const rows = buildExportRows(customers);
            const xlsx = new XlsxFile();
            xlsx.addWorksheet('customer', rows);
            const data = xlsx.toArrayBuffer();
            const brandName = brand?.name?.replace(/\s+/g, '_') ?? 'export';
            const filename = `Base_client_${brandName}.xlsx`;
            downloadFile(filename, data, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        } catch (error) {
            console.error('Failed to export customers:', error);
        } finally {
            setIsExporting(false);
        }
    }, [isExporting, fetchCustomersForExport, brand, buildExportRows]);

    const handleExportCsv = useCallback(async () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            const customers = await fetchCustomersForExport();
            const rows = buildExportRows(customers);
            const csvContent = rows
                .map((row) =>
                    row
                        .map((cell) => {
                            const cellStr = String(cell);
                            if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
                                return `"${cellStr.replace(/"/g, '""')}"`;
                            }
                            return cellStr;
                        })
                        .join(','),
                )
                .join('\n');
            const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
            const brandName = brand?.name?.replace(/\s+/g, '_') ?? 'export';
            const filename = `Base_client_${brandName}.csv`;
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to export customers:', error);
        } finally {
            setIsExporting(false);
        }
    }, [isExporting, fetchCustomersForExport, brand, buildExportRows]);

    const exportItems = useMemo(
        () => [
            { value: 'csv', label: 'CSV', onClick: () => handleExportCsv() },
            { value: 'xlsx', label: 'XLSX', onClick: () => handleExportXlsx() },
        ],
        [handleExportCsv, handleExportXlsx],
    );

    const {
        headers,
        rows,
        isLoading,
        handleRowClick,
        fetchData,
        fetchDataTotalCount,
        buttonLabel,
        selectedRestaurantId,
        restaurants,
        handleRestaurantChange,
        defaultHiddenColumns,
        showOptionsModal,
        handleOpenOptionModal,
        handleCloseOptionModal,
        customerFetchOptions,
        handleCustomerFetchOptionsChange,
    } = useCustomersTableVM({ brandId });

    const tabRoutes = React.useMemo(() => routes.filter(({ tab }) => tab === 'brands'), []);

    return (
        <RouterNavigationTabs routes={tabRoutes}>
            <ImportCustomersModal
                isOpen={showImportModal}
                onHide={handleCloseImportModal}
                brandId={brandId}
                onImportSuccess={handleImportSuccess}
            />
            <Container>
                <ActionsContainer>
                    <DropDown
                        items={exportItems}
                        type="text"
                        headerLabel="brand.customers.action.export"
                        button={
                            <ExportButton buttonType="primary" disabled={isExporting}>
                                <SvyIcon icon="download-line" />
                                <FormattedMessage id="brand.customers.action.export" />
                                <SvyIcon icon="arrow-down-s-line" />
                            </ExportButton>
                        }
                        isUnstyledButton
                    />
                    <Button buttonType="primary" onClick={handleOpenImportModal}>
                        <FormattedMessage id="brand.customers.action.import" />
                    </Button>
                </ActionsContainer>
                <HeaderContainer>
                    <Header>
                        <HeaderTitle>
                            <Title text="customers.page.title" />
                            <Subtitle>
                                <Text text="customers.page.description" />
                            </Subtitle>
                        </HeaderTitle>
                        <HeaderRight>
                            <OptionsButton onClick={handleOpenOptionModal} buttonType="secondary">
                                <SvyIcon icon="filter-line" />
                                <FormattedMessage id="customers.options" />
                            </OptionsButton>
                            {restaurants && (
                                <RestaurantSelector
                                    buttonLabel={buttonLabel}
                                    restaurants={restaurants}
                                    selected={selectedRestaurantId}
                                    onChange={handleRestaurantChange}
                                    headerLabel="orderList.filters.restaurantHeader"
                                />
                            )}
                        </HeaderRight>
                    </Header>
                    <Table
                        key={tableKey}
                        isSearchable
                        rows={rows}
                        emptyMessageId="table.noRowsAfterFilter"
                        columns={headers}
                        hasPagination
                        hasUrlNavigation
                        isSortable
                        onRowClick={handleRowClick}
                        isLoading={isLoading}
                        fetchData={fetchData}
                        fetchDataTotalCount={fetchDataTotalCount}
                        columnSelectorId="customerList"
                        defaultHiddenColumns={defaultHiddenColumns}
                    />
                    <OptionsModal
                        isOpen={showOptionsModal}
                        onHide={handleCloseOptionModal}
                        brandId={brandId}
                        customerFetchOptions={customerFetchOptions}
                        handleCustomerFetchOptionsChange={handleCustomerFetchOptionsChange}
                    />
                </HeaderContainer>
            </Container>
        </RouterNavigationTabs>
    );
};

export default Customer;
